import * as React from 'react'
import { debounce } from 'throttle-debounce'

type TUseResizeObserverOptions = {
  elements: (HTMLElement | undefined | null)[]
  onResize: (entries: ResizeObserverEntry[]) => void
  delay?: number
}

type TUseResizeObserverOutput = ResizeObserver | null

const createResizeObserver = (
  callback: (entries: ResizeObserverEntry[]) => void
) => {
  try {
    return new ResizeObserver(callback)
  } catch {
    return null
  }
}

export const useResizeObserver = ({
  delay = 500,
  elements,
  onResize
}: TUseResizeObserverOptions): TUseResizeObserverOutput => {
  // Keep the latest `onResize` in a ref so the observer is only recreated when
  // `delay` changes. Otherwise a callback whose identity changes on render (e.g.
  // `useSize`'s `handleResize`, which depends on `element`) would recreate and
  // re-subscribe the observer, and `ResizeObserver` fires its initial callback
  // on every `observe()` — a measure → setState → re-render → re-subscribe loop
  // that surfaces as "Maximum update depth exceeded".
  const onResizeRef = React.useRef(onResize)
  React.useLayoutEffect(() => {
    onResizeRef.current = onResize
  })

  const observer = React.useMemo(
    () =>
      createResizeObserver(
        debounce(delay, (entries: ResizeObserverEntry[]) =>
          onResizeRef.current(entries)
        )
      ),
    [delay]
  )

  React.useEffect(() => {
    elements.forEach((element) => {
      if (element) {
        observer?.observe(element)
      }
    })

    return () => {
      elements.forEach((element) => element && observer?.unobserve(element))
    }
  }, [observer, elements])

  return observer
}
